import useActionsCapabilitiesStore from '@/Features/Core/Components/ActionsCapabilities/store';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const useDragAndDrop = ({
  onChangeFile,
  setFileSelected,
  noVision,
  fileSelected,
}) => {
  const [dragActive, setDragActive] = useState(false);

  const {
    selectedModelParameters,
    setSelectedModelParameters,
    selectedModel,
    generationsModelsActive,
  } = useActionsCapabilitiesStore();

  const improveSelectingImage = (hasSelectedImage) => {
    // Check if the selected model already has attachment configured
    const modelHasAttachment = selectedModel?.parameters?.attachment;

    // If model already has attachment and we're adding an image, keep the attachment
    // If no image is selected, set attachment to undefined
    const shouldSetAttachment =
      hasSelectedImage || (modelHasAttachment && hasSelectedImage);

    setSelectedModelParameters({
      ...selectedModelParameters,
      attachment: shouldSetAttachment ? 1 : undefined,
    });
  };

  // Monitor changes in fileSelected to update attachment parameter
  useEffect(() => {
    if (fileSelected) {
      const hasSelectedImage = fileSelected.some(
        (file) => file.type.startsWith('image') || file.type === 'image'
      );
      improveSelectingImage(hasSelectedImage);
    }
  }, [fileSelected]);

  // Lista de tipos de archivos permitidos
  const allowedFileTypes = [
    'audio/mpeg',
    'video/mp4',
    'video/vnd.dlna.mpeg-tts',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/html',
    'text/csv',
    'application/json',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/x-python',
    'application/x-httpd-php',
    'text/javascript',
    'text/css',
    'text/x-csharp',
    'text/x-swift',
    'text/x-kotlin',
    'text/xml',
    'text/typescript',
    'text/markdown',
  ];

  /**
   * Maneja los eventos de drag
   * @param {Event} e - Evento de drag
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      // Verificar si realmente salimos del área de drop
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  };

  /**
   * Determina el tipo de archivo basado en su extensión
   * @param {File} file - Archivo a analizar
   * @returns {string} - Tipo MIME del archivo
   */
  const parseFileType = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    switch (extension) {
      case 'kt':
        return 'text/x-kotlin';
      case 'swift':
        return 'text/x-swift';
      case 'php':
        return 'application/x-httpd-php';
      case 'md':
        return 'text/markdown';
      default:
        return file.type;
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    // Small delay to ensure drag state is properly reset
    setTimeout(() => {
      setDragActive(false);
    }, 100);

    const items = e.dataTransfer.items;
    const files = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          let fileType = file.type;
          if (fileType == '') {
            fileType = parseFileType(file);
          }
          // Verificación específica de tipo de archivo
          if (file.type.startsWith('image/')) {
            const fileName = `image_${new Date()
              .toISOString()
              .replace(/[:.]/g, '-')}.${file.type.split('/')[1]}`;
            const fileWithMetadata = new File([file], fileName, {
              type: 'image',
            });

            files.push(fileWithMetadata);
          } else if (allowedFileTypes.includes(fileType)) {
            files.push(file);
          }
        }
      }
    }

    if (files.length > 0) {
      if (
        files.some((file) => file.type.startsWith('image')) &&
        noVision &&
        !generationsModelsActive
      ) {
        toast(
          'You must select a model that supports images as input, such as GPT-4o',
          {
            icon: '😱',
          }
        );
        return;
      }

      const simulatedEvent = {
        target: {
          files: files,
        },
      };
      await onChangeFile(simulatedEvent, true);
      setFileSelected((prevFiles) => [...prevFiles, ...files]);
      // The useEffect will handle updating the attachment parameter
    } else {
      toast('Unsupported file type', {
        icon: '⚠️',
      });
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    const files = [];
    const textItems = [];

    // Primero procesa los archivos de imagen
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        const fileName = `image_${new Date()
          .toISOString()
          .replace(/[:.]/g, '-')}.png`;
        const fileWithMetadata = new File([blob], fileName, {
          type: 'image', // Asegúrate de especificar el tipo correcto
        });
        files.push(fileWithMetadata);
        // The useEffect will handle updating the attachment parameter
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      if (noVision && !generationsModelsActive) {
        toast(
          'You must select a model that supports images as input, such as GPT-4o',
          {
            icon: '😱',
          }
        );
        return;
      }

      const simulatedEvent = {
        target: {
          files: files,
        },
      };
      await onChangeFile(simulatedEvent, true);
      setFileSelected((prevFiles) => [...prevFiles, ...files]);
    } else {
      for (let j = 0; j < items.length; j++) {
        if (items[j].kind === 'string') {
          textItems.push(
            new Promise((resolve) => {
              items[j].getAsString((str) => {
                const filteredStr = str
                  .replace(
                    /<img[^>]*src=["']data:image\/[^'"]*["'][^>]*>/gi,
                    ''
                  )
                  .replace(/<\/?[^>]+(>|$)/g, '');
                resolve(filteredStr);
              });
            })
          );
        }
      }
    }
  };

  // Limpiar el estado de drag al finalizar
  useEffect(() => {
    const handleWindowDragEnd = () => {
      setDragActive(false);
    };

    const handleWindowDragLeave = (e) => {
      // Solo desactivar si realmente salimos de la ventana
      if (
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        setDragActive(false);
      }
    };

    window.addEventListener('dragend', handleWindowDragEnd);
    window.addEventListener('drop', handleWindowDragEnd);
    window.addEventListener('dragleave', handleWindowDragLeave);

    return () => {
      window.removeEventListener('dragend', handleWindowDragEnd);
      window.removeEventListener('drop', handleWindowDragEnd);
      window.removeEventListener('dragleave', handleWindowDragLeave);
    };
  }, []);

  return {
    dragActive,
    handleDrag,
    handleDrop,
    handlePaste,
  };
};

export default useDragAndDrop;
